import { useEffect, useState } from 'react'
import {
  ApiError,
  fetchBootstrapTemplate,
  fetchDispatcherTemplate,
  fetchGrantTemplate,
  fetchRegionTemplate,
  adoptInstance,
  fetchInstances,
  rescanInstances,
} from '../api.js'
import type { AccountInstances } from '../api.js'
import type { AdoptionDetails } from '@qim/shared'
import type { AccountOnboarding, AccountSummary } from '../api.js'
import { assertNever, isAdoptionProblem, readAdoptionConfig } from '@qim/shared'
import type { TenantKind } from '@qim/shared'
import type { AccountTab } from '../routing.js'
import { hideRegion, restoreRegion } from '../api.js'
import { HideButton } from './hidden.jsx'
import { AccountSettingsPanel, QrveyBadge } from './account-settings.jsx'
import { BoundariesStep } from './boundaries-step.jsx'
import { Modal } from './modal.jsx'
import { ProtectionLock } from './lock.jsx'
import { AccountCost } from './account-cost.jsx'
import type {
  DiscoveredInstance,
  InstanceOrigin,
  InstanceState,
  OnboardingStep,
  StepState,
} from '@qim/shared'
import { formatInstant } from '../format.js'
import type { JSX } from 'react'

/**
 * One cloud account, and whether it is ready to deploy into.
 *
 * This screen replaces most of `docs/first-deployment-runbook.md` — a document
 * someone had to find, read and follow correctly. The control plane already knew
 * almost all of the answer; it just had nowhere to say it.
 *
 * ## Why the artifact and the instruction are in the same place
 *
 * Each step that needs a template offers that template. The runbook's failure
 * mode was an engineer reading step 3, going to fetch the artifact with curl, and
 * getting the parameters from memory. Putting the download on the step means the
 * thing to hand over and the reason for handing it over cannot drift apart.
 *
 * ## Why nothing here can extend a grant
 *
 * The grant is enforced by the customer's own cloud, in their own account, and
 * only their stack update moves it — which is exactly why it is enforced there
 * rather than here. So the step names their action and offers the template; a
 * button implying this console could do it would send someone looking for
 * something that cannot exist.
 *
 * (Deliberately vague about the mechanism: this file is in `web`, which the
 * boundary test keeps free of cloud vocabulary. The specifics live with the
 * adapter that implements them.)
 */

/** A word first, then a shape. Colour is third — see the status components. */
const STATE_WORD: Readonly<Record<StepState, string>> = {
  done: 'Done',
  attention: 'Needs attention',
  unknown: 'Unknown',
}

const STATE_MARK: Readonly<Record<StepState, string>> = {
  done: '✓',
  attention: '!',
  unknown: '?',
}

const StepStatus = ({ state }: { readonly state: StepState }): JSX.Element => (
  <span className={`state state-${state}`} role="status">
    <span aria-hidden="true">{STATE_MARK[state]}</span> {STATE_WORD[state]}
  </span>
)

/**
 * Downloads a template as a file.
 *
 * A file rather than text on the page: this gets handed to a customer, and
 * copying JSON out of a browser is how a template arrives truncated.
 */
const download = (name: string, template: unknown): void => {
  const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.click()
  URL.revokeObjectURL(url)
}

const TemplateButton = ({
  label,
  note,
  fetch,
  filename,
}: {
  readonly label: string
  readonly note?: string
  readonly fetch: () => Promise<{ readonly template: unknown }>
  readonly filename: string
}): JSX.Element => {
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | undefined>(undefined)

  const run = async (): Promise<void> => {
    setBusy(true)
    setFailure(undefined)
    try {
      download(filename, (await fetch()).template)
    } catch (error: unknown) {
      setFailure(
        error instanceof ApiError && error.status === 403
          ? 'You do not have the capability to hand over templates (account:onboard).'
          : 'Could not fetch the template just now.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="actions">
      {/* Ghost: handing over a template is a read, and the panel's own next
        action is the thing worth emphasising. */}
      <button type="button" className="ghost" onClick={() => void run()} disabled={busy}>
        {busy ? 'Fetching…' : label}
      </button>
      {note === undefined ? null : <span className="muted">{note}</span>}
      {failure === undefined ? null : <span className="failure">{failure}</span>}
    </div>
  )
}

/**
 * What is already in this account.
 *
 * ## Why the list is here before anyone presses anything
 *
 * A scan is a global bucket list plus a cluster list per region plus a describe
 * per cluster — the most expensive read in the product. It used to run only on
 * the button, which meant the panel was empty every time you opened the screen
 * and the answer cost several cross-account calls before you could see it. A
 * list like that is a list nobody opens.
 *
 * So the last scan is kept and this loads it: one read of Qrvey's own store, no
 * cross-account call. **Refresh** is what pays for a fresh one. The panel says
 * when it last looked, because a cache that does not admit its age is worse
 * than no cache.
 *
 * ## What it says, and what it refuses to say
 *
 * `running` is a live cluster; `residue` is storage with no cluster behind it,
 * usually a destroy that kept the buckets; `unknown` is a region we could not
 * read, which is not the same as a region with nothing in it. An instance that
 * has disappeared since the last scan stays, marked, because a destroyed
 * instance is the good outcome and a list identical before and after a clean-up
 * makes that work invisible.
 *
 * There is **no verdict column**. Nothing says orphan, stale, or delete this.
 * Most long-lived instances are correct — a customer's production instance is
 * permanent by definition — and only the reader knows whether a given one is a
 * forgotten demo. A screen that guesses gets ignored, including about the ones
 * that mattered. What it shows is origin, age and whether anyone has claimed it.
 */
export const InstanceScan = ({
  baseUrl,
  tenantId,
  accountKey,
  kind,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  /** The customer's kind, for the protection lock (IM-174). */
  readonly kind?: TenantKind | undefined
}): JSX.Element => {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<AccountInstances | undefined>(undefined)
  const [failure, setFailure] = useState<string | undefined>(undefined)
  const [adopting, setAdopting] = useState<string | undefined>(undefined)

  useEffect(() => {
    let live = true
    setLoading(true)
    void fetchInstances(baseUrl, tenantId, accountKey)
      .then((answer) => {
        if (live) setResult(answer)
      })
      .catch(() => {
        // A failed read of our own store is not a failed account, and the
        // Refresh button is still the way forward.
        if (live) setFailure('Could not read the last scan.')
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [baseUrl, tenantId, accountKey])

  const rescan = async (): Promise<void> => {
    setBusy(true)
    setFailure(undefined)
    try {
      setResult(await rescanInstances(baseUrl, tenantId, accountKey))
    } catch (error: unknown) {
      setFailure(
        error instanceof ApiError
          ? `The scan failed (${String(error.status)}).`
          : 'The scan could not be run.',
      )
    } finally {
      setBusy(false)
    }
  }

  const inventory = result?.inventory
  const instances = inventory?.instances ?? []
  // Answered beside the kept list by the same request, never per row (IM-174).
  const protections = new Map(
    (result?.protections ?? []).map((mark) => [mark.deploymentId, mark] as const),
  )

  return (
    <section className="panel">
      <h2>Instances in this account</h2>
      <p className="hint">
        Read from the account itself, so it finds instances this control plane has no record of.
        Refreshing makes several cross-account calls, which is why it happens when you ask.
      </p>

      <div className="actions">
        <button type="button" className="ghost" onClick={() => void rescan()} disabled={busy}>
          {busy ? 'Scanning…' : inventory === undefined ? 'Scan for instances' : 'Refresh'}
        </button>
        {inventory === undefined ? null : (
          <span className="muted">
            Last scanned{' '}
            <time dateTime={inventory.scannedAt}>{formatInstant(inventory.scannedAt)}</time>
          </span>
        )}
        {failure === undefined ? null : <span className="failure">{failure}</span>}
      </div>

      {result?.unreachable === true ? (
        /* Not an empty list: that reads as a clean account, which is the one
           thing a failed scan must not appear to say. Anything kept from an
           earlier scan is still shown below, and still true as of its date. */
        <p role="alert">
          <span aria-hidden="true">!</span> Could not read this account just now. Anything below is
          from the last scan that worked.
        </p>
      ) : null}

      {loading ? (
        <p role="status">Loading…</p>
      ) : inventory === undefined ? (
        <p role="status">This account has not been scanned yet.</p>
      ) : instances.length === 0 ? (
        <p role="status">No Qrvey instances found in this account.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">Deployment</th>
                <th scope="col">Region</th>
                <th scope="col">State</th>
                <th scope="col">Origin</th>
                {/* Named for its source since IM-129, because the fleet now
                  carries an age of its own derived from run records. The two
                  answer the same question from different evidence — this one is
                  the cluster's own creation time, which is the better answer
                  where there is one, and an age that silently means two
                  different things in two places is one nobody can act on. */}
                <th scope="col">Cluster created</th>
                <th scope="col">Found by</th>
                <th scope="col"> </th>
              </tr>
            </thead>
            <tbody>
              {instances.map((instance, i) => (
                <tr key={`${instance.deploymentId}-${instance.region}-${String(i)}`}>
                  <td>
                    {instance.deploymentId === '' ? '—' : instance.deploymentId}
                    <ProtectionLock
                      mark={
                        instance.deploymentId === ''
                          ? undefined
                          : protections.get(instance.deploymentId)
                      }
                      kind={kind}
                    />
                  </td>
                  <td>{instance.region === '' ? '—' : instance.region}</td>
                  {/* A word, then a shape. Colour is third — this list gets read
                    on a projector and screenshotted into tickets. */}
                  <td>
                    {instance.goneSince === undefined ? (
                      <span className={`state state-${STATE_OF[instance.state]}`} role="status">
                        <span aria-hidden="true">{MARK_OF[instance.state]}</span>{' '}
                        {WORD_OF[instance.state]}
                      </span>
                    ) : (
                      /* Kept on purpose: a destroyed instance is the good
                         outcome, and dropping the row would make the clean-up
                         invisible. */
                      <span className="muted">
                        Gone since{' '}
                        <time dateTime={instance.goneSince}>
                          {formatInstant(instance.goneSince)}
                        </time>
                      </span>
                    )}
                  </td>
                  {/* "Nobody has claimed this" is a fact, not an accusation. It
                    is the row worth a second look, and nothing more. */}
                  <td>{ORIGIN_WORD[instance.origin]}</td>
                  <td>
                    {instance.createdAt === undefined ? '—' : formatInstant(instance.createdAt)}
                  </td>
                  <td className="muted">{evidenceOf(instance)}</td>
                  <td>
                    {/*
                      Nothing to bring in for a row that is not an instance.
                      An unreadable region produces a row with no deployment id
                      — it describes the scan, not something in the account —
                      and offering to adopt it was visible only in a browser.
                    */}
                    {instance.origin !== 'external' ||
                    instance.goneSince !== undefined ||
                    instance.deploymentId === '' ? null : (
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => {
                          setAdopting(instance.deploymentId)
                        }}
                      >
                        Bring in
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adopting === undefined ? null : (
        <AdoptInstance
          baseUrl={baseUrl}
          tenantId={tenantId}
          accountKey={accountKey}
          deploymentId={adopting}
          onDone={() => {
            setAdopting(undefined)
            void rescan()
          }}
          onCancel={() => {
            setAdopting(undefined)
          }}
        />
      )}
    </section>
  )
}

/**
 * Bringing an instance in by uploading the config it was deployed with.
 *
 * ## The file never leaves this machine
 *
 * A `config.json` carries `account_config.access_key_id`, `secret_access_key`
 * and `session_token` — live AWS credentials for the customer's account — plus
 * the registry key and whatever else the chart takes. The control plane's
 * central promise is that it never holds those.
 *
 * So the file is read here, `readAdoptionConfig` takes five non-secret fields,
 * and only those are sent. Nothing else is uploaded: the `File` is read with
 * `text()` in the browser and never attached to a request. Uploading it whole
 * and stripping server-side would be simpler and would break the promise —
 * by then the credentials would already be in Qrvey's account, in a request
 * log, in memory.
 *
 * What is about to be sent is shown before it is sent, so the person handing
 * over the file can see exactly what travels.
 */
const AdoptInstance = ({
  baseUrl,
  tenantId,
  accountKey,
  deploymentId,
  onDone,
  onCancel,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  readonly deploymentId: string
  readonly onDone: () => void
  readonly onCancel: () => void
}): JSX.Element => {
  const [details, setDetails] = useState<AdoptionDetails | undefined>(undefined)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  const read = async (file: File): Promise<void> => {
    setProblem(undefined)
    setDetails(undefined)
    let parsed: unknown
    try {
      parsed = JSON.parse(await file.text())
    } catch {
      // "Not JSON" and "not a Qrvey config" are different things to be told.
      setProblem('That file is not valid JSON.')
      return
    }
    const fromFile = readAdoptionConfig(parsed)
    if (isAdoptionProblem(fromFile)) {
      setProblem(fromFile.problem)
      return
    }
    if (fromFile.deploymentId !== deploymentId) {
      // The likeliest mistake with several configs open, and the one that would
      // point an adoption at the wrong instance's state.
      setProblem(
        `That config is for ${fromFile.deploymentId}, not ${deploymentId}. Adopting it here would record the wrong state location.`,
      )
      return
    }
    setDetails(fromFile)
  }

  const submit = async (): Promise<void> => {
    if (details === undefined) return
    setBusy(true)
    try {
      await adoptInstance(baseUrl, tenantId, accountKey, details)
      onDone()
    } catch (error: unknown) {
      setProblem(
        error instanceof ApiError ? `Refused (${String(error.status)}).` : 'Could not record it.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    /*
     * A modal like the other forms (IM-146), and for a reason of its own: the
     * panel used to open inside the instances table, pushing every row below it
     * down while somebody was reading the list they had just scanned.
     */
    <Modal title={`Bring in ${deploymentId}`} onCancel={onCancel}>
      <p className="hint">
        Upload the <code>config.json</code> this instance was deployed with. It is read here, in
        your browser: the deployment id, region, state location and the instance&rsquo;s non-secret
        variables are sent, and everything below is what travels. The AWS credentials, registry key,
        API keys and private keys inside it never leave this machine.
      </p>

      <div className="field">
        <label htmlFor="adoptConfig">config.json</label>
        <input
          id="adoptConfig"
          type="file"
          accept="application/json,.json"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file !== undefined) void read(file)
          }}
        />
      </div>

      {problem === undefined ? null : (
        <p role="alert" className="failure">
          {problem}
        </p>
      )}

      {details === undefined ? null : (
        <>
          {/* Shown before it is sent: the person handing over the file can see
            exactly what travels, which is the claim this panel makes. */}
          <dl className="facts">
            <dt>Region</dt>
            <dd>{details.region}</dd>
            <dt>State bucket</dt>
            <dd>{details.stateBucket}</dd>
            <dt>State key</dt>
            <dd>{details.stateKey}</dd>
            <dt>Chart version</dt>
            <dd>{details.chartVersion ?? 'Not in the file'}</dd>
            <dt>Variables</dt>
            <dd>
              {details.variables === undefined ? (
                'None the file carried, or all of them were secret'
              ) : (
                <>
                  {Object.keys(details.variables).join(', ')}
                  {/* Named rather than counted, and the values go with them —
                    the panel's claim is that this list *is* what travels, so a
                    count would make it a claim the reader cannot check. */}
                  <p className="muted">
                    &hellip;and their values, so the first run against this instance is not typed
                    from scratch. Anything secret-shaped was removed before this list was built.
                  </p>
                </>
              )}
            </dd>
          </dl>
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              Cancel
            </button>
            <button type="button" className="primary" onClick={() => void submit()} disabled={busy}>
              {busy ? 'Recording…' : 'Bring it in'}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}

/** Reuses the step palette: running is done, residue needs a look, unknown is unknown. */
const STATE_OF: Readonly<Record<InstanceState, StepState>> = {
  running: 'done',
  residue: 'attention',
  unknown: 'unknown',
}

const WORD_OF: Readonly<Record<InstanceState, string>> = {
  running: 'Running',
  residue: 'Storage only',
  unknown: 'Unknown',
}

const MARK_OF: Readonly<Record<InstanceState, string>> = {
  running: '\u2713',
  residue: '!',
  unknown: '?',
}

/**
 * Where an instance came from — a different question from whether it runs.
 *
 * "No record" rather than "orphan" or "untracked": it states what we know about
 * our own records and leaves the conclusion to the reader, who has the context.
 */
const ORIGIN_WORD: Readonly<Record<InstanceOrigin, string>> = {
  managed: 'Ours',
  adopted: 'Brought in',
  external: 'No record',
}

/**
 * Why this row is here.
 *
 * Shown because a bare list invites trust it has not earned: a reader who can
 * see it was a bucket match and not a cluster can recognise a false positive
 * themselves, which is the honest alternative to claiming there are none.
 */
const evidenceOf = (instance: DiscoveredInstance): string => {
  if (instance.evidence.cluster !== undefined) {
    return instance.evidence.clusterStatus === undefined
      ? 'Cluster'
      : `Cluster (${instance.evidence.clusterStatus})`
  }
  const n = instance.evidence.buckets.length
  return n === 0 ? 'Region unreadable' : `${String(n)} bucket${n === 1 ? '' : 's'}`
}

export const Account = ({
  baseUrl,
  tenantId,
  onboarding,
  role,
  onChanged,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly onboarding: AccountOnboarding
  /** Whose capabilities decide whether the hide control is offered (IM-114). */
  readonly role: string
  /** Re-reads the screen, so a hidden region disappears without a refresh. */
  readonly onChanged: () => void
}): JSX.Element => {
  const { assessment } = onboarding

  /*
   * The regions this account is set up in, or undefined when the API predates
   * IM-95 and cannot answer per region.
   *
   * Undefined and empty mean different things and are rendered differently:
   * undefined falls back to the whole-account view below, while empty is a real
   * and common state — roles in place, no region set up — which is where every
   * engagement starts and is not a fault.
   */
  const account = onboarding.account

  /**
   * Whether we could read the account at all.
   *
   * Taken from the access step's own state rather than recomputed here: the API
   * decides what the facts mean, and the console renders the answer — the same
   * split that keeps the screen and the API from disagreeing about readiness.
   */
  const unreadable = account?.accountSteps.some(
    (step) => step.id === 'access-template' && step.state === 'unknown',
  )
  const regionNames = account?.regions.map((r) => r.region) ?? []
  const [selected, setSelected] = useState<string | undefined>(regionNames[0])
  /*
   * The selected region, falling back to the first.
   *
   * The fallback is not only for the initial render: `selected` is state, so it
   * survives this screen being pointed at a different account, and that
   * account may well not have the region the last one did. Resolving it by
   * lookup means a stale name lands on a real region rather than on an empty
   * panel.
   */
  const current = account?.regions.find((r) => r.region === selected) ?? account?.regions[0]

  /**
   * A tab's badge: the worst state among that region's steps.
   *
   * Worst rather than an average or a count, because the tab is a summary
   * someone scans to decide where to look — and `unknown` outranks `attention`
   * for the same reason it does everywhere here: a region we could not read is
   * not a region we know is merely untidy.
   */
  const worstOf = (steps: readonly OnboardingStep[]): StepState =>
    steps.some((s) => s.state === 'unknown')
      ? 'unknown'
      : steps.some((s) => s.state === 'attention')
        ? 'attention'
        : 'done'

  /**
   * One step. `region` is the tab it is being shown under, and undefined for
   * the account's own steps.
   *
   * It is passed down rather than read off the step because the assessment's
   * prose does not carry one: `nextAction` says "ask the customer to update the
   * dispatcher stack", which was unambiguous when an account had one. Under a
   * tab, with three regions each having a stack of that name, it is not — and
   * the region the panel is showing is the answer, so the panel says it rather
   * than every string in `onboarding.ts` being rewritten to repeat it.
   */
  const renderStep = (step: OnboardingStep, region?: string): JSX.Element => (
    <li key={step.id}>
      <h3>
        {step.title} <StepStatus state={step.state} />
      </h3>
      {step.detail === '' ? (
        // An unreadable step has nothing to report, and inventing a detail
        // would read as a finding.
        <p className="muted">Could not read this from the account.</p>
      ) : (
        <p>{step.detail}</p>
      )}
      {step.nextAction === undefined ? null : (
        <p className="next">
          {step.nextAction}
          {region === undefined ? '' : ` This is about ${region}.`}
        </p>
      )}
      {step.state === 'done' ? null : templateFor(step, region)}
    </li>
  )

  const templateFor = (step: OnboardingStep, region?: string): JSX.Element | null => {
    /*
     * Which region to deploy it in, said out loud.
     *
     * The dispatcher template itself is the same document for every region —
     * its resources are all region-local and land wherever the stack is
     * created — so the button does not change. What changes is the instruction,
     * and "deploy this" with no region is how an account ends up with two
     * dispatchers in one region and none in the other.
     */
    const where = region === undefined ? '' : ` Deploy it in ${region}.`
    if (step.id === 'access-template') {
      return (
        <TemplateButton
          label="Download access template"
          // No longer "and the state backend": IM-93 moved the bucket, the lock
          // table and the log group into the region stack, because they are
          // regional and this one is not.
          note="Hand this to the customer. It creates the diagnostics role and the deploy role. Once per account."
          filename={`qrvey-access-${onboarding.accountKey.replace(':', '-')}.json`}
          fetch={() => fetchBootstrapTemplate(baseUrl, tenantId, onboarding.accountKey)}
        />
      )
    }
    if (step.id === 'grant') {
      return (
        <TemplateButton
          label="Download grant template"
          // Its own stack since IM-94, and this step offered the *dispatcher*
          // template until IM-98 — the wrong stack for a lapsed grant, which is
          // the single most common reason a run is refused.
          // The last sentence is IM-155: letting the grant lapse stops runs and
          // not reads, which is the one part of the consent model a customer
          // does not expect. `docs/bootstrap-compatibility.md` says it at length.
          note="Hand this to the customer. It sets the window runs may start in, for every region. Nothing here can extend it: the expiry is a condition in their own account, so only their stack update moves it. It gates runs only: Qrvey's read-only diagnostics come from the access stack and continue until that stack is deleted."
          filename={`qrvey-grant-${onboarding.accountKey.replace(':', '-')}.json`}
          fetch={() => fetchGrantTemplate(baseUrl, tenantId, onboarding.accountKey)}
        />
      )
    }
    if (step.id === 'region-stack') {
      return (
        <TemplateButton
          label="Download region template"
          // This step could not exist until the region stack's own version was
          // read (it publishes `/qrvey/region/version` and always has, and
          // nothing looked at it). A region on an old stack read as fully set
          // up, and the artifact that fixes it was only reachable from the
          // "add a region" panel — which nobody opens for a region they
          // already have.
          note={`Hand this to the customer.${where} It holds the Terraform state bucket, the lock table and the run log group.`}
          filename={`qrvey-region-${onboarding.accountKey.replace(':', '-')}.json`}
          fetch={() => fetchRegionTemplate(baseUrl, tenantId, onboarding.accountKey)}
        />
      )
    }
    if (
      step.id === 'dispatcher' ||
      step.id === 'completions' ||
      // The registry secret is built by the dispatcher stack (IM-59), so this
      // is the artifact that fixes this step. It offered nothing at all until
      // IM-98, which is the runbook failure IM-58 set out to end.
      step.id === 'registry-credentials' ||
      // The redactor is inlined in the dispatcher's task definition, so
      // updating that stack is the only thing that moves its rules (IM-84).
      step.id === 'redaction'
    ) {
      return (
        <TemplateButton
          label="Download dispatcher template"
          // Worth saying out loud: this request is what grants the account
          // permission to publish onto Qrvey's run bus, so fetching it repairs
          // that wiring even when the customer changes nothing.
          note={`Hand this to the customer.${where} Fetching it also repairs this account's permission to report run completions to Qrvey.`}
          filename={`qrvey-dispatcher-${onboarding.accountKey.replace(':', '-')}.json`}
          fetch={() => fetchDispatcherTemplate(baseUrl, tenantId, onboarding.accountKey)}
        />
      )
    }
    return null
  }

  /**
   * The artifact that adds a region.
   *
   * Not reachable from `templateFor`, because there is no step to hang it on:
   * the region steps live *inside* a region, and this is what you need when
   * there are none. That is the commonest state at the start of an engagement,
   * and until IM-98 the screen said "send the customer the region template" and
   * then offered the dispatcher one.
   *
   * It names no region, and that is accurate rather than a gap — the template's
   * resources land wherever the stack is created, so the region is chosen at
   * deploy time and there is nothing here to pre-select.
   */
  /**
   * Adding a region takes **two** stacks, and this hands over both.
   *
   * It used to offer only the region template. That creates the state bucket,
   * which is what `listAccountRegions` reads — so the region appeared in the
   * list and looked added, while nothing could actually be deployed into it:
   * the dispatcher is what receives a run, and it was never mentioned. Somebody
   * followed the screen exactly and ended up with two regions they could not
   * use.
   *
   * They stay two stacks rather than one because they change at completely
   * different rates — the dispatcher is on its sixth version and the region
   * template has never changed — and merging would put a customer's Terraform
   * state backend inside the template their security team re-reads on every
   * dispatcher bump. That is a real reason, but it is our reason, and it is no
   * excuse for making someone guess at the second step.
   *
   * Numbered, because the order matters: the dispatcher's task definition
   * refers to the state bucket by name, so a runner started before the region
   * stack exists has nowhere to keep state.
   */
  const addRegion = (heading: string, owed: boolean): JSX.Element => (
    /*
     * A disclosure, open only when the account actually needs a region.
     *
     * It used to sit open in both places, styled with the same numbered
     * `.steps` list as the onboarding checklist above it — deliberately, so it
     * would look consistent. Beside a finished account that consistency is the
     * bug: Amit asked why a fully set-up account was "still asking" him to
     * deploy both templates. Nothing was asking him, but an offer shaped like a
     * checklist is read as a checklist.
     *
     * So the shape follows the answer to "is this owed?". With no regions it
     * is — the account has the roles and nowhere to deploy — and collapsing it
     * would hide the one thing left to do. With a region already set up it is
     * an offer, and the run panel's rule applies: an action someone may never
     * take does not sit open beside a status list.
     *
     * What is inside is unchanged either way — two stacks, in order, because a
     * region genuinely needs both.
     */
    <details className="add-region" open={owed}>
      <summary>{heading}</summary>
      <p className="hint">
        Two stacks, in this order. A region with only the first appears in this list and cannot run
        anything &mdash; the dispatcher is what a deployment is actually sent to.
      </p>
      <ol className="steps">
        <li>
          <h3>Region</h3>
          <p>The state bucket, the lock table and the run log group.</p>
          <TemplateButton
            label="Download region template"
            note="Hand this to the customer. Whichever region they deploy it in becomes a region they can deploy into."
            filename={`qrvey-region-${onboarding.accountKey.replace(':', '-')}.json`}
            fetch={() => fetchRegionTemplate(baseUrl, tenantId, onboarding.accountKey)}
          />
        </li>
        <li>
          <h3>Dispatcher</h3>
          <p>The runner and the Lambda that starts it. Deploy it in the same region.</p>
          <TemplateButton
            label="Download dispatcher template"
            note="Hand this to the customer. Fetching it also repairs this account's permission to report run completions to Qrvey."
            filename={`qrvey-dispatcher-${onboarding.accountKey.replace(':', '-')}.json`}
            fetch={() => fetchDispatcherTemplate(baseUrl, tenantId, onboarding.accountKey)}
          />
        </li>
      </ol>
    </details>
  )

  return (
    <>
      <section className="panel">
        <h2>
          {onboarding.label} <QrveyBadge kind={onboarding.tenantKind} />
        </h2>
        <p className="muted">{onboarding.accountKey}</p>

        {/* Said above the steps, because "this account takes no new instances"
          changes what every step below it is worth doing. */}
        {onboarding.settings?.newInstancesBlocked?.value === true ? (
          <p role="status" className="hint">
            This account is set not to take new instances. That refuses a new instance from this
            console only &mdash; see the Settings tab.
          </p>
        ) : null}

        {/*
          The question someone about to click a run button is actually asking —
          answered before they click it rather than by the refusal.

          Since IM-91 that question names a region, so this names the regions it
          is true of. An account ready in one region and not another is neither
          "ready" nor "not ready", and saying either would send someone to the
          wrong place.
        */}
        {account === undefined ? (
          assessment.canRun ? (
            <p role="status">
              <span aria-hidden="true">✓</span> Ready — a run started now would reach this account.
            </p>
          ) : (
            <p role="alert">
              <span aria-hidden="true">!</span> Not ready — a run started now would fail.
              {assessment.nextAction === undefined ? null : ` ${assessment.nextAction}`}
            </p>
          )
        ) : unreadable ? (
          /*
           * Unreadable is not empty, and saying "no regions set up yet" about an
           * account we cannot see is a claim we have no basis for. The two send
           * someone to different places: one to deploy a region, the other to
           * find out why we cannot look (IM-121).
           *
           * For a brand-new account this is the *expected* first state — the
           * access template has not been deployed yet, so there is no role to
           * assume — and the step below offers exactly that template.
           */
          <p role="status">
            <span aria-hidden="true">?</span> Could not read this account, so what is set up in it
            is unknown. If it was registered a moment ago, the access template below is the first
            step.
          </p>
        ) : account.regions.length === 0 ? (
          <p role="status">
            <span aria-hidden="true">!</span> No regions set up yet — there is nowhere to deploy
            into.
          </p>
        ) : account.canRunSomewhere ? (
          <p role="status">
            <span aria-hidden="true">✓</span> Ready in{' '}
            {account.regions
              .filter((r) => r.canRun)
              .map((r) => r.region)
              .join(', ')}
            .
            {account.regions.some((r) => !r.canRun)
              ? ` Needs attention in ${account.regions
                  .filter((r) => !r.canRun)
                  .map((r) => r.region)
                  .join(', ')}.`
              : ''}
          </p>
        ) : (
          <p role="alert">
            <span aria-hidden="true">!</span> Not ready in any region — a run started now would
            fail.
            {account.regions[0]?.nextAction === undefined
              ? null
              : ` ${account.regions[0].nextAction}`}
          </p>
        )}

        {/*
          There was a "Runner image: …" line here, reporting the account-level
          facts read — which is one region's dispatcher, the control plane's
          own, presented as though it were the account's. Harmless while
          nothing else mentioned the image; actively wrong since IM-123, which
          checks it per region and can therefore contradict it. The dispatcher
          step says it where the fact actually lives.
        */}
      </section>

      {account === undefined ? (
        // An API that predates IM-95. One list, as before.
        <section className="panel">
          <h2>Onboarding</h2>
          <ol className="steps">{assessment.steps.map((step) => renderStep(step))}</ol>
        </section>
      ) : (
        <>
          <section className="panel">
            <h2>This account</h2>
            <p className="hint">
              Asked once. IAM is global, so the roles and the grant are the same wherever you
              deploy.
            </p>
            <ol className="steps">{account.accountSteps.map((step) => renderStep(step))}</ol>
          </section>

          {onboarding.boundaries === undefined ? null : (
            <BoundariesStep
              baseUrl={baseUrl}
              tenantId={tenantId}
              accountKey={onboarding.accountKey}
              coverage={onboarding.boundaries}
              role={role}
              requiredTemplate={
                <TemplateButton
                  label="Download access template requiring boundaries"
                  note="Every instance here has its boundary. Update the access stack with this, or set DeployRoleBoundary to required on it: every apply then needs its instance’s boundary."
                  filename={`qrvey-access-${onboarding.accountKey.replace(':', '-')}.json`}
                  fetch={() =>
                    fetchBootstrapTemplate(baseUrl, tenantId, onboarding.accountKey, true)
                  }
                />
              }
            />
          )}

          <section className="panel">
            <h2>Regions</h2>
            {account.regions.length === 0 ? (
              /*
               * Not an error, and the common state at the start of an
               * engagement: the roles are in place and no region is set up.
               * `role="status"` rather than `alert` for exactly that reason.
               */
              <>
                <p role="status">No regions set up yet.</p>
                <p className="hint">
                  This account has the access roles but nowhere to deploy. The templates below add a
                  region.
                </p>
                {addRegion('Add a region', true)}
              </>
            ) : (
              <>
                {/*
                  Buttons in a labelled group, and `aria-current` rather than
                  `role="tab"`. A real tab widget promises arrow-key navigation
                  between the tabs and Tab out to the panel; claiming the role
                  without implementing that is worse for a screen-reader user
                  than plain buttons, which already work. The class is a
                  literal string so `styles.test.ts` sees it — a name built in
                  a ternary is invisible to that scan, which is how a class
                  goes unstyled without failing anything.
                */}
                <div className="tabs" role="group" aria-label="Regions">
                  {account.regions.map((r) => (
                    <button
                      key={r.region}
                      type="button"
                      className="tab"
                      aria-current={r.region === current?.region ? 'true' : undefined}
                      onClick={() => {
                        setSelected(r.region)
                      }}
                    >
                      {r.region} <StepStatus state={worstOf(r.steps)} />
                    </button>
                  ))}
                </div>
                {current === undefined ? null : (
                  <>
                    <ol className="steps">
                      {current.steps.map((step) => renderStep(step, current.region))}
                    </ol>
                    {/*
                      Under the selected region's steps rather than on the tab
                      itself: a tab is for choosing, and a control inside one is
                      a control someone hits while trying to look.
                    */}
                    <HideButton
                      role={role}
                      hidden={false}
                      what="region"
                      name={current.region}
                      confirm
                      onHide={() =>
                        hideRegion(baseUrl, tenantId, onboarding.accountKey, current.region)
                      }
                      onRestore={() =>
                        restoreRegion(baseUrl, tenantId, onboarding.accountKey, current.region)
                      }
                      onChanged={onChanged}
                    />
                  </>
                )}
                {/*
                  Adding a *second* region is the case IM-91 exists for, and it
                  is reachable from an account that already has one — so the
                  template belongs here too, not only in the empty state. Below
                  the tabs rather than inside one: it adds a region, it does not
                  fix the region being shown.
                */}
                {addRegion('Add another region', false)}
              </>
            )}
          </section>
        </>
      )}

      {/* The kept scan, the cost and the settings are tabs of their own since
        the secondary navigation — see `AccountSection`. */}
    </>
  )
}

/**
 * The account's name, for the tabs that are not Onboarding.
 *
 * Onboarding leads with a readiness panel that carries the name; the other tabs
 * have no such panel, and a page that never says which account it is about is
 * one somebody acts on in the wrong account.
 */
const AccountTitle = ({
  account,
  kind,
}: {
  readonly account: AccountSummary
  readonly kind?: TenantKind | undefined
}): JSX.Element => (
  <section className="panel">
    <h2>
      {account.label} <QrveyBadge kind={kind} />
    </h2>
    <p className="muted">{account.accountKey}</p>
  </section>
)

/**
 * An account tab other than Onboarding.
 *
 * Rendered from the account *listing* — one read of Qrvey's own table — rather
 * than the onboarding assessment, which reads into the customer's account. So
 * opening the Cost or Settings tab makes no cross-account call at all: the
 * IM-83 rule, that what is not shown is not fetched, applied to a tab.
 */
export const AccountSection = ({
  tab,
  baseUrl,
  tenantId,
  account,
  kind,
  role,
  onChanged,
}: {
  readonly tab: Exclude<AccountTab, 'onboarding'>
  readonly baseUrl: string
  readonly tenantId: string
  readonly account: AccountSummary
  readonly kind?: TenantKind | undefined
  readonly role: string
  readonly onChanged: () => void
}): JSX.Element => {
  const body = ((): JSX.Element | null => {
    switch (tab) {
      case 'instances':
        return (
          <InstanceScan
            baseUrl={baseUrl}
            tenantId={tenantId}
            accountKey={account.accountKey}
            kind={kind}
          />
        )
      case 'cost':
        return (
          <AccountCost
            baseUrl={baseUrl}
            tenantId={tenantId}
            accountKey={account.accountKey}
            role={role}
          />
        )
      case 'settings':
        return (
          <AccountSettingsPanel
            baseUrl={baseUrl}
            tenantId={tenantId}
            accountKey={account.accountKey}
            {...(account.settings === undefined ? {} : { settings: account.settings })}
            kind={kind}
            role={role}
            onChanged={onChanged}
          />
        )
      default:
        return assertNever(tab)
    }
  })()
  return (
    <>
      <AccountTitle account={account} kind={kind} />
      {body}
    </>
  )
}
